import { IconArrowRight, IconBooks, IconCalendarEvent, IconClockHour4, IconMapPin, IconPhone, IconSpeakerphone, IconUser } from '@tabler/icons-react';
import { useTranslation } from '../../../../i18n/hooks/useTranslation';
import { PosterLogo, PosterSheet, fs, hexToRgba, numberScale, posterFields, shopIsLong, valueFitScale } from '../posterParts';

/**
 * Landscape "Split" — a colour panel on the left (logo, notice badge, the organisation and the vacancy count as the hero) and a clean
 * panel on the right (the two dates, qualification, age, call-to-action), with the shop in a full-width strip underneath.
 * Palette: hero, heroDeep, accent, bg, ink, muted, line, cta, footer, footerText, footerName.
 */
const FONT = "'Anek Bangla', 'Noto Sans Bengali', sans-serif";

function Tile({ p, Icon, label, hot = false, children }) {
    return (
        <div className="flex items-center" style={{ background: '#fff', borderRadius: fs(1.6), borderLeft: `${fs(0.9)} solid ${hot ? p.accent : p.hero}`, padding: `${fs(1.5)} ${fs(2)}`, gap: fs(1.8), boxShadow: '0 0.4cqw 1cqw rgba(15,23,42,0.1)' }}>
            <div className="flex flex-none items-center justify-center rounded-full" style={{ width: fs(5.2), height: fs(5.2), background: hot ? p.accent : p.hero }}>
                <Icon style={{ width: fs(3), height: fs(3), color: hot ? p.ink : '#fff' }} stroke={2.2} />
            </div>
            <div className="min-w-0 flex-1">
                <div style={{ color: p.muted, fontSize: fs(1.7), fontWeight: 700, lineHeight: 1.2 }}>{label}</div>
                {children}
            </div>
        </div>
    );
}

export default function LandscapeSplitDesign({ job, business, theme, showFooter = true, className = '' }) {
    const { t } = useTranslation();
    const p = theme.palette;
    const f = posterFields(job, business, t);
    const eduScale = valueFitScale(f.edu, 54);
    const ageScale = valueFitScale(f.age, 40);

    const body = (
        <div className="grid flex-1" style={{ gridTemplateColumns: '40cqw 1fr' }}>
            <div
                className="relative flex flex-col"
                style={{
                    padding: `${fs(3.4)} 3.4cqw ${fs(2.8)}`,
                    gap: fs(1.8),
                    color: '#fff',
                    background: `repeating-linear-gradient(135deg, rgba(255,255,255,0.05) 0 1.2cqw, rgba(255,255,255,0) 1.2cqw 2.4cqw), linear-gradient(160deg, ${p.hero} 0%, ${p.heroDeep} 100%)`,
                }}
            >
                <div className="flex items-center justify-between" style={{ gap: fs(2) }}>
                    <PosterLogo job={job} size={11} style={{ background: '#fff', borderRadius: '50%', border: `${fs(0.5)} solid ${p.accent}`, padding: fs(1), boxShadow: '0 0.5cqw 1.2cqw rgba(0,0,0,0.3)' }} />
                    <div className="flex items-center" style={{ background: p.accent, color: p.ink, borderRadius: '99px', padding: `${fs(0.6)} ${fs(1.8)}`, gap: fs(0.9), fontSize: fs(1.9), fontWeight: 900 }}>
                        <IconSpeakerphone style={{ width: fs(2.3), height: fs(2.3) }} stroke={2.4} />
                        <div>{f.label.notice}</div>
                    </div>
                </div>
                <div className="flex flex-1 flex-col justify-center">
                    <div style={{ fontSize: fs(4.7), fontWeight: 900, lineHeight: 1.12 }}>{f.org}</div>
                    {f.position && <div style={{ fontSize: fs(2.3), fontWeight: 600, opacity: 0.9, lineHeight: 1.3, marginTop: fs(0.9) }}>{f.position}</div>}
                </div>
                <div>
                    <div style={{ width: fs(5), height: '0.4cqw', background: p.accent, borderRadius: '99px', marginBottom: fs(1) }} />
                    <div style={{ fontSize: fs(2), fontWeight: 800, opacity: 0.95 }}>{f.label.vacancy}</div>
                    <div className="flex items-end" style={{ gap: fs(1.2) }}>
                        <div style={{ color: p.accent, fontSize: `calc(${12 * numberScale(f.vacancy)}cqw * var(--fs, 1))`, fontWeight: 900, lineHeight: 1 }}>{f.vacancy}</div>
                        <div style={{ fontSize: fs(2.8), fontWeight: 800, paddingBottom: fs(1) }}>{f.label.unit}</div>
                    </div>
                </div>
            </div>

            <div className="flex min-w-0 flex-col justify-center" style={{ background: p.bg, padding: `${fs(3)} 3.8cqw ${fs(2.6)} 3.4cqw`, gap: fs(1.8) }}>
                <div className="grid grid-cols-2" style={{ gap: fs(1.8) }}>
                    <Tile p={p} Icon={IconCalendarEvent} label={f.label.start}>
                        <div style={{ color: p.ink, fontSize: fs(2.45), fontWeight: 800, lineHeight: 1.2, whiteSpace: 'nowrap' }}>{f.start}</div>
                    </Tile>
                    <Tile p={p} Icon={IconClockHour4} label={f.label.end} hot>
                        <div style={{ color: p.ink, fontSize: fs(2.45), fontWeight: 900, lineHeight: 1.2, whiteSpace: 'nowrap' }}>{f.end}</div>
                    </Tile>
                </div>
                <Tile p={p} Icon={IconBooks} label={f.label.edu}>
                    <div style={{ color: p.ink, fontSize: `calc(${2.7 * eduScale}cqw * var(--fs, 1))`, fontWeight: 800, lineHeight: 1.25 }}>{f.edu}</div>
                </Tile>
                <Tile p={p} Icon={IconUser} label={f.label.age}>
                    <div style={{ color: p.ink, fontSize: `calc(${2.7 * ageScale}cqw * var(--fs, 1))`, fontWeight: 800, lineHeight: 1.25 }}>{f.age}</div>
                </Tile>
                <div style={{ color: p.muted, fontSize: fs(1.7), fontWeight: 600 }}>✓ {f.label.promo}</div>
                <div className="flex items-center justify-between" style={{ background: p.cta, color: '#fff', borderRadius: fs(1.8), padding: `${fs(1.5)} ${fs(2.4)}`, boxShadow: '0 0.6cqw 1.4cqw rgba(0,0,0,0.2)' }}>
                    <div style={{ fontSize: fs(2.9), fontWeight: 900 }}>{f.label.cta}</div>
                    <IconArrowRight className="flex-none" style={{ width: fs(3.6), height: fs(3.6), color: p.accent }} stroke={3} />
                </div>
            </div>
        </div>
    );

    const footer = showFooter ? (
        <div className="flex items-center justify-between" style={{ background: p.footer, color: p.footerText, padding: `${fs(1.5)} 4cqw`, gap: `${fs(0.8)} ${fs(2.4)}`, flexWrap: shopIsLong(f.shop) ? 'wrap' : 'nowrap' }}>
            <div className="flex min-w-0 items-center" style={{ gap: fs(1.6) }}>
                {f.shop.logo && <img src={f.shop.logo} alt="" className="flex-none rounded-full object-contain" style={{ width: fs(5.4), height: fs(5.4), background: '#fff', padding: '0.4cqw' }} />}
                <div style={{ color: p.footerName, fontSize: fs(3.3), fontWeight: 900, lineHeight: 1.12, opacity: f.shop.placeholder ? 0.65 : 1 }}>{f.shop.nameShown}</div>
            </div>
            <div className="flex min-w-0 items-center justify-center" style={{ flex: shopIsLong(f.shop) ? '1 0 100%' : '1 1 0%', order: shopIsLong(f.shop) ? 3 : 0, gap: fs(0.8), fontSize: fs(2.1), lineHeight: 1.25, opacity: f.shop.addressPlaceholder ? 0.6 : 0.92 }}>
                <IconMapPin className="flex-none" style={{ width: fs(2.4), height: fs(2.4), color: p.footerName }} />
                <div>{f.shop.addressShown}</div>
            </div>
            {f.shop.phone && (
                <div className="flex flex-none items-center" style={{ gap: fs(0.8), fontSize: fs(3), fontWeight: 900 }}>
                    <IconPhone className="flex-none" style={{ width: fs(2.8), height: fs(2.8), color: p.footerName }} />
                    <div>{f.shop.phone}</div>
                </div>
            )}
        </div>
    ) : null;

    return <PosterSheet landscape deps={[job, business, theme, showFooter, t]} className={className} fontFamily={FONT} background={p.bg} body={body} footer={footer} />;
}
